import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import EnFilterButtonBase from 'Components/datagrid/filterButtons/EnFilterButtonBase.vue';
import EnFilterTypeSelectButton from 'Components/datagrid/filterButtons/EnFilterTypeSelectButton.vue';
import EnAutocomplete from 'Components/inputs/EnAutocomplete/EnAutocomplete.vue';
import { FilterEvent } from '../types/filterEvent';
import { useFilter } from '../composables/filterButtonComposable';
import { FilterType } from 'Js/enums/filterType.js';
/**
 * A filter button for a multiple values.
 */
export default /*@__PURE__*/ _defineComponent({
    __name: 'EnMultipleAutocompleteFilterButton',
    props: /*@__PURE__*/ _mergeModels({
        /**
         * The key that is used to generate the id for the input fields.
         */
        type: String,
        initialType: {
            type: String,
            default: FilterType.Equal,
        },
        hint: String,
        label: String,
        filterTypeSelectionVisible: Boolean,
        applyButtonText: String,
        resetButtonText: String,
        resetFilter: Function,
        noFilterText: String,
        id: String,
    }, {
        "modelValue": {},
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(['update:modelValue', FilterEvent.Input, FilterEvent.FilterReset, 'update:type', FilterEvent.FilterUpdated], ["update:modelValue"]),
    setup(__props, { expose: __expose, emit: __emit }) {
        __expose();
        const componentProps = __props;
        /**
         * The values of the filter fields (v-model).
         * Properties per filter:
         *    label: the displayed label of the field.
         *    value: the actual value of the field.
         *    type: the filter type of the field.
         *    apiUrl: api url of the auto complete
         *    property: path to the property used for querying. E.g. 'ConsumptionPoint.City'
         *    searchExpression: function that takes a search term and returns a devextreme filter, used if apiUrl is set
         *
         *    @type{{
         *      label: String,
         *      value: {String, Number},
         *      type: String,
         *      apiUrl: String,
         *      property: String,
         *      searchExpression: Function
         *    }}
         */
        const modelValue = _useModel(__props, 'modelValue');
        const emit = __emit;
        const { t } = useI18n();
        const { 
        // Data
        displayString, menuIsVisible, changedValue, selectedType, isSingleFilter, 
        // Computed
        noFilterTextComputed, resetButtonTextComputed, applyButtonTextComputed, 
        // Methods
        typeItemsNumber, typeItemsString, returnTypeId, returnFilterButtonId, returnId, emitUpdate, } = useFilter(componentProps, { emit }, modelValue, updateDisplayString);
        const fieldKeys = computed(() => Object.keys(modelValue.value));
        const currentOption = computed({
            get() {
                return modelValue.value;
            },
            set(value) {
                emit(FilterEvent.Input, value);
                emit('update:modelValue', value);
                applyFilters(value);
            },
        });
        watch(modelValue, (value) => {
            updateDisplayString(value);
        }, { deep: true });
        function getDisplayString(item, fieldKey) {
            if (typeof item === 'string') {
                return item;
            }
            return item[currentOption.value[fieldKey].property] ? item[currentOption.value[fieldKey].property] : '';
        }
        function hasAutofocus(index, fieldKey) {
            return index === 0 && (currentOption.value[fieldKey].value === undefined || currentOption.value[fieldKey].value === null);
        }
        function resetFilter() {
            fieldKeys.value.forEach(fieldKey => {
                currentOption.value[fieldKey].value = currentOption.value[fieldKey].resetValue;
            });
            componentProps.resetFilter?.();
        }
        function reloadOnFocusIn(index, fieldKey) {
            return currentOption.value[fieldKey].value === undefined || currentOption.value[fieldKey].value === null;
        }
        function onResetButtonClicked() {
            Object.keys(currentOption.value).forEach(key => currentOption.value[key].value = null);
            // we can't use changed value in this component, because we need to bind to value directly 
            // in order to use the values of one search field as parameters in the search request for another field.
            // we set 'this.changedValue = this.value' so that the emitUpdate from filterMixin works correctly
            changedValue.value = modelValue.value;
            updateDisplayString(modelValue.value);
            menuIsVisible.value = false;
            emitUpdate(changedValue.value);
        }
        function applyFilters() {
            applyFiltersDoClose(true);
        }
        function applyFiltersWithoutClosing() {
            applyFiltersDoClose(false);
        }
        function applyFiltersDoClose(doClose) {
            changedValue.value = currentOption.value;
            updateDisplayString(changedValue.value);
            menuIsVisible.value = !doClose;
            emitUpdate(changedValue.value);
        }
        function updateDisplayString(fieldDefinitions) {
            let displayStringArray = [];
            fieldKeys.value.forEach(fieldKey => {
                const fieldValue = fieldDefinitions[fieldKey].value;
                if (fieldDefinitions[fieldKey] && Boolean(fieldValue)) {
                    displayStringArray.push(fieldValue);
                }
            });
            displayString.value = displayStringArray.join(' ').trim() || noFilterTextComputed.value;
        }
        onBeforeMount(() => {
            isSingleFilter.value = false;
        });
        const __returned__ = { componentProps, modelValue, emit, t, displayString, menuIsVisible, changedValue, selectedType, isSingleFilter, noFilterTextComputed, resetButtonTextComputed, applyButtonTextComputed, typeItemsNumber, typeItemsString, returnTypeId, returnFilterButtonId, returnId, emitUpdate, fieldKeys, currentOption, getDisplayString, hasAutofocus, resetFilter, reloadOnFocusIn, onResetButtonClicked, applyFilters, applyFiltersWithoutClosing, applyFiltersDoClose, updateDisplayString, EnFilterButtonBase, EnFilterTypeSelectButton, EnAutocomplete };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
